import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { forkJoin } from 'rxjs';
import { FORMLY_FIELD_TYPES } from '@app/constants';
import { AuthService } from '@app/core/services';
import { AccountTypeDescEnum } from '@app/enums';
import { TableConfig } from '@app/interfaces';
import { TableComponent, TitleButton, TitleButtonsComponent, TitleComponent } from '@app/shared/components';
import { FormComponent } from '@app/shared/forms/components';
import { AccountStructureService, UnitService } from '@app/shared/services';

@Component({
	selector: 'sb-account-setup',
	standalone: true,
	imports: [MatCardModule, FormComponent, TableComponent, TitleComponent, MatTabsModule, TitleButtonsComponent],
	templateUrl: './account-setup.component.html',
	styleUrl: './account-setup.component.less',
})
export class AccountSetupComponent implements OnInit {
	@ViewChild('table', { static: true }) tableComponent: TableComponent;

	constructor(
		private readonly accountStructureService: AccountStructureService,
		private readonly unitService: UnitService,
		private readonly authService: AuthService,
	) {}

	saveDisabled = true;
	accountOptions: FormlyFormOptions = {
		formState: {
			mode: 'edit',
			readOnly: false,
			flyout: false,
			selects: {
				siteElements: [],
				fundElements: [],
			},
		},
	};
	accountForm: UntypedFormGroup = new UntypedFormGroup({});

	accountFields: FormlyFieldConfig[] = [];

	accountMappingModel: any = {
		SiteElement: null,
		FundElement: null,
	};

	data = [
		{
			name: 'Cash Account',
			structureType: AccountTypeDescEnum.Asset,
			key: 'CashAcctMask',
			template: '',
			mask: '',
			elementMap: '',
		},
		{
			name: 'A/P Account',
			structureType: AccountTypeDescEnum.Liability,
			key: 'APAcctMask',
			template: '',
			mask: '',
			elementMap: '',
		},
		{
			name: 'Fund Equity Account',
			structureType: AccountTypeDescEnum.Reserve,
			key: 'FundEquityAcctMask',
			template: '',
			mask: '',
			elementMap: '',
		},
		{
			name: 'Sales Tax Expense Account',
			key: 'STEAcctMask',
			structureType: AccountTypeDescEnum.Expense,
			template: '',
			mask: '',
			elementMap: '',
		},
	];

	accountStructureData = [
		{
			structureType: AccountTypeDescEnum.Asset,
			mask: '',
			elementMap: '',
		},
		{
			structureType: AccountTypeDescEnum.Liability,
			mask: '',
			elementMap: '',
		},
		{
			structureType: AccountTypeDescEnum.Reserve,
			mask: '',
			elementMap: '',
		},
		{
			structureType: AccountTypeDescEnum.Expense,
			mask: '',
			elementMap: '',
		},
		{
			structureType: AccountTypeDescEnum.Revenue,
			mask: '',
			elementMap: '',
		},
	];

	titleButtons: TitleButton[] = [
		{
			label: 'Save Changes',
			onClick: () => this.saveTemplateChanges(),
			disabled: () => this.saveDisabled,
			color: 'primary',
			cssClass: 'linq-button linq-button--primary',
		},
	];

	accountStructureTableConfig: TableConfig = this.getAccountStructureTableConfig();

	tableConfig: TableConfig = this.getAccountOffsetTableConfig();

	accountStructureModel: any = null;
	questionMarkMask: string;

	ngOnInit(): void {
		this.loadDependencies();
	}

	templateIsValid = (control: AbstractControl): ValidationErrors | null => {
		if (!control) {
			return null;
		}

		const templateValue = control.value ?? '';

		const parent = control.parent;
		if (!parent) {
			return null;
		}

		const structureType = parent.get('structureType')?.value;

		if (!this.accountStructureModel) {
			return null;
		}

		let mask = '';
		if (structureType === AccountTypeDescEnum.Asset) {
			mask = this.accountStructureModel?.AssetAccountMask ?? '';
		} else if (structureType === AccountTypeDescEnum.Liability) {
			mask = this.accountStructureModel?.LiabilityAccountMask ?? '';
		} else if (structureType === AccountTypeDescEnum.Reserve) {
			mask = this.accountStructureModel?.ReserveAccountMask ?? '';
		} else if (structureType === AccountTypeDescEnum.Expense) {
			mask = this.accountStructureModel?.ExpenseAccountMask ?? '';
		} else if (structureType === AccountTypeDescEnum.Revenue) {
			mask = this.accountStructureModel?.RevenueAccountMask ?? '';
		} else {
			mask = this.accountStructureModel?.AssetAccountMask ?? '';
		}

		if (!mask) {
			return null;
		}

		const isValid = this.validateTemplateAgainstMask(mask, templateValue);

		if (isValid) {
			return null;
		} else {
			return { templateIsInValid: true };
		}
	};

	validateTemplateAgainstMask(mask: string, value: string): boolean {
		if (!mask || value === '') {
			return false;
		}

		const maskParts = mask.split('.');
		const valueParts = value.split('.');

		if (maskParts.length !== valueParts.length) {
			return false;
		}

		for (let i = 0; i < maskParts.length; i++) {
			if (valueParts[i].length !== maskParts[i].length) {
				return false;
			}
		}

		return true;
	}

	onFieldChanged(fieldGroups: any) {
		let allFormsValid = true;

		fieldGroups.forEach((formGroup: any) => {
			if (formGroup?.invalid) {
				if (formGroup.controls) {
					Object.keys(formGroup.controls).forEach((key) => {
						const control = formGroup.controls[key];
						if (control.errors) {
							allFormsValid = false;
						}
					});
				} else {
					allFormsValid = false;
				}
			}
		});

		this.saveDisabled = !allFormsValid;
		this.titleButtons = [...this.titleButtons];
	}

	loadDependencies() {
		forkJoin([this.accountStructureService.getStructure(), this.unitService.getUnit()]).subscribe(([accountStructureResponse, unitResponse]) => {
			this.accountStructureModel = accountStructureResponse.accountStructure;
			this.accountStructureModel.unit = unitResponse.unit;
			this.accountMappingModel.SiteElement = this.accountStructureModel.unit?.SiteElement ?? null;
			this.accountMappingModel.FundElement = this.accountStructureModel.unit?.FundElement ?? null;
			this.questionMarkMask = (accountStructureResponse?.accountStructure?.UnifiedAccountMask ?? '').replace(/0/g, '?');
			this.accountOptions.formState.selects.siteElements = this.getSiteElements().map((element) => {
				return {
					label: element,
					value: element,
				};
			});
			this.accountOptions.formState.selects.fundElements = this.getFundElements().map((element) => {
				return {
					label: element,
					value: element,
				};
			});
			this.accountFields = this.loadElementFields();
			this.data.forEach((item) => {
				if (item.structureType === AccountTypeDescEnum.Asset) {
					item.template = this.accountStructureModel?.unit?.CashAcctMask ?? '';
					item.mask = this.accountStructureModel?.AssetAccountMask ?? '';
					item.elementMap = this.accountStructureModel?.AssetElementMap ?? '';
				} else if (item.structureType === AccountTypeDescEnum.Liability) {
					item.template = this.accountStructureModel?.unit?.APAcctMask ?? '';
					item.mask = this.accountStructureModel?.LiabilityAccountMask ?? '';
					item.elementMap = this.accountStructureModel?.LiabilityElementMap ?? '';
				} else if (item.structureType === AccountTypeDescEnum.Reserve) {
					item.template = this.accountStructureModel?.unit?.FundEquityAcctMask ?? '';
					item.mask = this.accountStructureModel?.ReserveAccountMask ?? '';
					item.elementMap = this.accountStructureModel?.ReserveElementMap ?? '';
				} else if (item.structureType === AccountTypeDescEnum.Expense) {
					item.template = this.accountStructureModel?.unit?.STEAcctMask ?? '';
					item.mask = this.accountStructureModel?.ExpenseAccountMask ?? '';
					item.elementMap = this.accountStructureModel?.ExpenseElementMap ?? '';
				}
			});

			this.accountStructureData.forEach((item) => {
				if (item.structureType === AccountTypeDescEnum.Asset) {
					item.mask = this.accountStructureModel?.AssetAccountMask ?? '';
					item.elementMap = this.accountStructureModel?.AssetElementMap ?? '';
				} else if (item.structureType === AccountTypeDescEnum.Liability) {
					item.mask = this.accountStructureModel?.LiabilityAccountMask ?? '';
					item.elementMap = this.accountStructureModel?.LiabilityElementMap ?? '';
				} else if (item.structureType === AccountTypeDescEnum.Reserve) {
					item.mask = this.accountStructureModel?.ReserveAccountMask ?? '';
					item.elementMap = this.accountStructureModel?.ReserveElementMap ?? '';
				} else if (item.structureType === AccountTypeDescEnum.Expense) {
					item.mask = this.accountStructureModel?.ExpenseAccountMask ?? '';
					item.elementMap = this.accountStructureModel?.ExpenseElementMap ?? '';
				} else if (item.structureType === AccountTypeDescEnum.Revenue) {
					item.mask = this.accountStructureModel?.RevenueAccountMask ?? '';
					item.elementMap = this.accountStructureModel?.RevenueElementMap ?? '';
				}
			});

			if (this.authService.authentication$?.value?.useNetAccounts) {
				this.accountStructureData.push({
					structureType: AccountTypeDescEnum.Net,
					mask: this.accountStructureModel?.NetAccountMask ?? '',
					elementMap: this.accountStructureModel?.NetElementMap ?? '',
				});
			}

			// Refresh the table data so that any changes to the account structure are reflected in the UI.
			this.accountStructureData = [...this.accountStructureData];
		});
	}

	private getElementsArray(map: string | null): string[] {
		return map === null ? [] : map.split('.');
	}

	getFundElements() {
		if (!this.accountStructureModel) {
			return [];
		}

		const elementArrays = [this.getElementsArray(this.accountStructureModel.AssetElementMap)];

		let elements = elementArrays.slice(1).reduce((acc, curr) => acc.filter((x) => curr.includes(x)), elementArrays[0] ?? []);

		if (this.accountStructureModel.useNetAccounts) {
			const netElements = this.getElementsArray(this.accountStructureModel.NetElementMap);
			elements = elements.filter((x) => netElements.includes(x));
		}

		const siteElement = this.accountStructureModel.unit?.SiteElement;
		return elements.filter((element) => element !== siteElement);
	}

	getSiteElements() {
		if (!this.accountStructureModel) {
			return [];
		}

		const elementArrays = [this.getElementsArray(this.accountStructureModel.AssetElementMap)];

		let elements = elementArrays.slice(1).reduce((acc, curr) => acc.filter((x) => curr.includes(x)), elementArrays[0] ?? []);

		if (this.accountStructureModel.useNetAccounts) {
			const netElements = this.getElementsArray(this.accountStructureModel.NetElementMap);
			elements = elements.filter((x) => netElements.includes(x));
		}

		const fundElement = this.accountStructureModel.unit?.FundElement;
		return elements.filter((element) => element !== fundElement);
	}

	loadElementFields(): FormlyFieldConfig[] {
		return [
			{
				key: 'FundElement',
				type: FORMLY_FIELD_TYPES.select,
				className: 'col-xs-3',
				props: {
					label: 'Fund Element',
				},
				expressions: {
					'props.options': (field) => {
						const siteSelected = field.form.get('SiteElement')?.value;
						const allFundElements = field.options?.formState?.selects?.fundElements ?? [];
						return siteSelected ? allFundElements.filter((opt: any) => opt.value !== siteSelected) : allFundElements;
					},
				},
			},
			{
				key: 'SiteElement',
				type: FORMLY_FIELD_TYPES.select,
				className: 'col-xs-3',
				props: {
					label: 'Site Element',
				},
				expressions: {
					'props.options': (field) => {
						const fundSelected = field.form.get('FundElement')?.value;
						const allSiteElements = field.options?.formState?.selects?.siteElements ?? [];
						return fundSelected ? allSiteElements.filter((opt: any) => opt.value !== fundSelected) : allSiteElements;
					},
				},
			},
			{
				key: 'submitChanges',
				type: FORMLY_FIELD_TYPES.button,
				wrappers: [],
				className: 'col-xs-3',
				props: {
					type: 'button',
					buttonClass: 'linq-button linq-button--primary',
					text: 'Save Changes',
					onClick: () => this.saveAccountFormChanges(),
				},
				expressions: {
					'props.disabled': '!field.form.dirty',
				},
			},
		];
	}

	cancelChanges(): void {
		this.loadDependencies();
	}

	saveAccountFormChanges(): void {
		this.accountStructureModel.unit.SiteElement = this.accountMappingModel.SiteElement;
		this.accountStructureModel.unit.FundElement = this.accountMappingModel.FundElement;

		this.unitService.saveUnit(this.accountStructureModel.unit).subscribe(() => {
			this.loadDependencies();
			this.accountForm.markAsPristine();
		});
	}

	saveTemplateChanges(): void {
		this.accountStructureModel.unit.CashAcctMask = this.data.find((x) => x.key === 'CashAcctMask')?.template ?? '';
		this.accountStructureModel.unit.APAcctMask = this.data.find((x) => x.key === 'APAcctMask')?.template ?? '';
		this.accountStructureModel.unit.FundEquityAcctMask = this.data.find((x) => x.key === 'FundEquityAcctMask')?.template ?? '';
		this.accountStructureModel.unit.STEAcctMask = this.data.find((x) => x.key === 'STEAcctMask')?.template ?? '';
		this.unitService.saveUnit(this.accountStructureModel.unit).subscribe(() => {
			this.loadDependencies();
			this.accountForm.markAsPristine();
			this.saveDisabled = true;
			this.titleButtons = [...this.titleButtons];
		});
	}

	getAccountStructureTableConfig(): TableConfig {
		return {
			columns: [
				{
					key: 'structureType',
					header: 'Structure Type',
				},
				{
					key: 'mask',
					header: 'Account Mask',
				},
				{
					key: 'elementMap',
					header: 'Element Map',
				},
			],
			defaultSortDirection: 'asc',
			defaultSortField: 'structureType',
			selectedId: null,
		};
	}

	getAccountOffsetTableConfig(): TableConfig {
		return {
			columns: [
				{
					key: 'name',
					header: 'Name',
				},
				{
					key: 'structureType',
					header: 'Structure Type',
				},
				{
					key: 'template',
					header: 'Template',
					template: 'input',
					templateConfig: {
						validators: [Validators.required, (control: AbstractControl): ValidationErrors | null => this.templateIsValid(control)],
						validatorMessages: {
							required: 'Template is required.',
							templateIsInValid: 'Template must match the account structure.',
						},
					},
				},
			],
			editableRows: true,
			editActions: {
				idProperty: 'name',
				allowDelete: () => false,
				update: (row: any): void => {
					row.template = row.template.trim();
				},
			},
			defaultSortDirection: 'asc',
			defaultSortField: 'name',
			selectedId: null,
		};
	}
}
